import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarIntegration = ({ selectedService, onBooking }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPrincipal, setSelectedPrincipal] = useState(null);

  // Mock calendar data
  const availableDates = [
    { date: '2025-01-20', day: 'Monday', slots: ['09:00', '11:00', '14:00', '16:00'] },
    { date: '2025-01-21', day: 'Tuesday', slots: ['10:00', '13:00', '15:00'] },
    { date: '2025-01-22', day: 'Wednesday', slots: ['09:00', '11:00', '14:00', '16:00'] },
    { date: '2025-01-23', day: 'Thursday', slots: ['10:00', '13:00', '15:00'] },
    { date: '2025-01-24', day: 'Friday', slots: ['09:00', '11:00', '14:00'] }
  ];

  const principals = [
    {
      id: 'sarah-chen',
      name: 'Sarah Chen',
      title: 'Principal Engineer - Architecture',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      specialties: ['Microservices', 'Cloud Architecture', 'Scalability'],
      experience: '15+ years',
      description: 'Specializes in large-scale system architecture and cloud-native transformations.'
    },
    {
      id: 'marcus-rodriguez',
      name: 'Marcus Rodriguez',
      title: 'Principal Engineer - Velocity',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      specialties: ['DevOps', 'CI/CD', 'Team Velocity'],
      experience: '12+ years',
      description: 'Expert in development velocity optimization and team transformation.'
    },
    {
      id: 'elena-vasquez',
      name: 'Elena Vasquez',
      title: 'Principal Engineer - Culture',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      specialties: ['Team Culture', 'Ownership', 'Process Optimization'],
      experience: '14+ years',
      description: 'Focuses on building ownership culture and high-performing engineering teams.'
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleBooking = () => {
    if (selectedDate && selectedTime && selectedPrincipal) {
      const bookingData = {
        date: selectedDate,
        time: selectedTime,
        principal: selectedPrincipal,
        service: selectedService
      };
      onBooking(bookingData);
    }
  };

  const isBookingComplete = selectedDate && selectedTime && selectedPrincipal;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
          <Icon name="Calendar" size={32} color="var(--color-accent)" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Schedule Your Discovery Call
        </h2>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Book a 60-minute discovery call with one of our principal engineers to discuss your 
          specific challenges and create a customized transformation plan.
        </p>
      </div>

      {/* Principal Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">
          Choose Your Principal Engineer
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {principals.map((principal) => (
            <div
              key={principal.id}
              onClick={() => setSelectedPrincipal(principal)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPrincipal?.id === principal.id
                  ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
              }`}
            >
              <div className="flex items-start space-x-3">
                <img
                  src={principal.avatar}
                  alt={principal.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-text-primary">{principal.name}</h4>
                  <p className="text-sm text-accent font-medium">{principal.title}</p>
                  <p className="text-xs text-text-secondary mt-1">{principal.experience}</p>
                </div>
              </div>
              
              <p className="text-sm text-text-secondary mt-3 mb-3">
                {principal.description}
              </p>
              
              <div className="flex flex-wrap gap-1">
                {principal.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-muted text-xs text-text-secondary rounded"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Date Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">
          Select Date
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {availableDates.map((dateOption) => (
            <div
              key={dateOption.date}
              onClick={() => {
                setSelectedDate(dateOption.date);
                setSelectedTime(null); // Reset time when date changes
              }}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                selectedDate === dateOption.date
                  ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
              }`}
            >
              <div className="text-sm font-medium text-text-primary">
                {dateOption.day}
              </div>
              <div className="text-lg font-semibold text-text-primary">
                {new Date(dateOption.date).getDate()}
              </div>
              <div className="text-xs text-text-secondary">
                {new Date(dateOption.date).toLocaleDateString('en-US', { month: 'short' })}
              </div>
              <div className="text-xs text-accent mt-1">
                {dateOption.slots.length} slots
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary">
            Select Time
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {availableDates
              .find(d => d.date === selectedDate)
              ?.slots.map((timeSlot) => (
                <div
                  key={timeSlot}
                  onClick={() => setSelectedTime(timeSlot)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    selectedTime === timeSlot
                      ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
                  }`}
                >
                  <div className="font-medium text-text-primary">
                    {formatTime(timeSlot)}
                  </div>
                  <div className="text-xs text-text-secondary">
                    60 minutes
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Booking Summary */}
      {isBookingComplete && (
        <div className="bg-accent/5 rounded-lg border border-accent/20 p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Booking Summary
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Icon name="User" size={16} color="var(--color-accent)" />
              <span className="text-text-primary">
                {selectedPrincipal.name} - {selectedPrincipal.title}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Calendar" size={16} color="var(--color-accent)" />
              <span className="text-text-primary">
                {formatDate(selectedDate)}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Clock" size={16} color="var(--color-accent)" />
              <span className="text-text-primary">
                {formatTime(selectedTime)} (60 minutes)
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Video" size={16} color="var(--color-accent)" />
              <span className="text-text-primary">
                Video call (link will be sent via email)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Call Preparation */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Call Preparation
        </h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-1" />
            <div>
              <h4 className="font-medium text-text-primary">Discovery Call Agenda</h4>
              <p className="text-sm text-text-secondary">
                Current challenges, technical environment, team dynamics, and transformation goals
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-1" />
            <div>
              <h4 className="font-medium text-text-primary">Technical Assessment</h4>
              <p className="text-sm text-text-secondary">
                Architecture review, velocity metrics, and technical debt evaluation
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-1" />
            <div>
              <h4 className="font-medium text-text-primary">Transformation Roadmap</h4>
              <p className="text-sm text-text-secondary">
                Customized approach, timeline, and expected outcomes discussion
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Signals */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <Icon name="Shield" size={20} color="var(--color-success)" />
          <div>
            <h4 className="font-medium text-text-primary">Enterprise Security</h4>
            <p className="text-xs text-text-secondary">SOC 2 Type II Certified</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <Icon name="FileText" size={20} color="var(--color-success)" />
          <div>
            <h4 className="font-medium text-text-primary">NDA Available</h4>
            <p className="text-xs text-text-secondary">Confidentiality Guaranteed</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <Icon name="Clock" size={20} color="var(--color-success)" />
          <div>
            <h4 className="font-medium text-text-primary">No Commitment</h4>
            <p className="text-xs text-text-secondary">Discovery Call is Free</p>
          </div>
        </div>
      </div>

      {/* Book Button */}
      <div className="text-center">
        <Button
          variant="default"
          size="lg"
          className="bg-accent hover:bg-accent/90"
          iconName="Calendar"
          iconPosition="left"
          onClick={handleBooking}
          disabled={!isBookingComplete}
        >
          Confirm Discovery Call
        </Button>
        <p className="text-xs text-text-secondary mt-2">
          You'll receive a confirmation email with the meeting link and preparation materials
        </p>
      </div>
    </div>
  );
};

export default CalendarIntegration;